import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import './WhyAIUniversityPage.scss';

export const WhyAIUniversityPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showBackButton, setShowBackButton] = useState(false);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const sections = [
    {
      id: 'intro',
      headline: 'College is a scam.',
      subtext: 'Four years. Six-figure debt. Outdated curriculum.',
      className: 'intro-section',
    },
    {
      id: 'broken',
      headline: 'One size fits none.',
      subtext:
        'Packed lecture halls. Generic curriculums. Your unique potential, ignored.',
      className: 'broken-section',
    },
    {
      id: 'obsolete',
      headline: "Teaching yesterday's skills.",
      subtext:
        'By the time you graduate, half of what you learned is obsolete.',
      className: 'obsolete-section',
    },
    {
      id: 'debt',
      headline: 'The debt trap.',
      subtext: 'Average student loan: $37,574. Time to pay off: 20 years.',
      className: 'debt-section',
    },
    {
      id: 'collapse',
      headline: 'Traditional education is collapsing.',
      subtext: 'While tuition skyrockets, the value plummets.',
      className: 'collapse-section',
    },
    {
      id: 'personalized',
      headline: 'Education that adapts to you.',
      subtext: 'AI learns how you learn. Every lesson, tailored to your mind.',
      className: 'personalized-section',
    },
    {
      id: 'future',
      headline: 'The future of education is here.',
      subtext: 'AI-powered. Personalized. Affordable.',
      className: 'future-section',
    },
    {
      id: 'revolution',
      headline: 'Welcome to the future.',
      subtext: 'Odyssey. Where learning meets innovation.',
      className: 'revolution-section',
    },
  ];

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const maxScrollTop = documentHeight - windowHeight;

          // Simple progress calculation
          const progress = maxScrollTop > 0 ? scrollY / maxScrollTop : 0;
          setScrollProgress(Math.min(Math.max(progress, 0), 1));

          // Simple section calculation - just based on scroll position
          const sectionIndex = Math.floor(scrollY / windowHeight);
          setActiveSection(
            Math.min(Math.max(sectionIndex, 0), sections.length - 1)
          );

          ticking = false;
        });
        ticking = true;
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      setShowBackButton(e.clientY < 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [sections.length]);

  const getSectionStyles = (index: number) => {
    const isActive = index === activeSection;
    const distance = Math.abs(index - activeSection);

    if (isActive) {
      return {
        opacity: 1,
        transform: 'translateY(0) scale(1)',
        zIndex: 10,
        pointerEvents: 'auto' as const,
        filter: 'blur(0px) brightness(1)',
      };
    } else {
      return {
        opacity: Math.max(0, 0.8 - distance * 0.4),
        transform: `translateY(0) scale(${0.9 - distance * 0.05})`,
        zIndex: 10 - distance,
        pointerEvents: 'none' as const,
        filter: `blur(${distance * 1}px) brightness(${0.7 - distance * 0.1})`,
      };
    }
  };

  const getVisualStyles = (index: number) => {
    const isActive = index === activeSection;
    const distance = Math.abs(index - activeSection);

    if (isActive) {
      return {
        opacity: 1,
        transform: 'perspective(1000px) rotateY(0deg) rotateX(0deg) scale(1)',
        transition: 'all 1.2s cubic-bezier(0.23, 1, 0.32, 1)',
        filter: 'brightness(1) contrast(1)',
      };
    } else {
      const rotation = (index - activeSection) * 25;
      return {
        opacity: Math.max(0, 0.8 - distance * 0.3),
        transform: `perspective(1000px) rotateY(${rotation}deg) rotateX(${distance * 10}deg) scale(${0.7 + 0.3 / (distance + 1)})`,
        transition: 'all 1.2s cubic-bezier(0.23, 1, 0.32, 1)',
        filter: `brightness(${0.5 + 0.5 / (distance + 1)}) contrast(${0.8 + 0.2 / (distance + 1)})`,
      };
    }
  };

  return (
    <div className="why-ai-university-page" ref={containerRef}>
      <div
        className="scroll-progress-indicator"
        style={{ width: `${scrollProgress * 100}%` }}
      />

      <button
        className={`back-button ${showBackButton ? 'visible' : ''}`}
        onClick={() => navigate(-1)}
        aria-label="Go back"
      >
        <ArrowLeft size={20} />
        <span>Back</span>
      </button>

      {sections.map((section, index) => (
        <section
          key={section.id}
          ref={(el: HTMLDivElement | null) => (sectionRefs.current[index] = el)}
          className={`scroll-section ${section.className}`}
          style={getSectionStyles(index)}
        >
          <div className="content-wrapper">
            <div className="text-content">
              <h1 className="headline">{section.headline}</h1>
              <p className="subtext">{section.subtext}</p>
            </div>

            <div className="visual-content" style={getVisualStyles(index)}>
              {index === 0 && (
                <div className="visual-element broken-system">
                  <div className="debt-counter">
                    <span className="debt-label">Average Debt</span>
                    <span className="debt-amount">$37,574</span>
                  </div>
                  <div className="broken-pieces">
                    <div className="piece piece-1">📚</div>
                    <div className="piece piece-2">🎓</div>
                    <div className="piece piece-3">💸</div>
                  </div>
                </div>
              )}

              {index === 1 && (
                <div className="visual-element broken-visual">
                  <div className="classroom-grid">
                    <div className="student student-1">🧑‍🎓</div>
                    <div className="student student-2">👩‍🎓</div>
                    <div className="student student-3">👨‍🎓</div>
                    <div className="student student-4">🧑‍🎓</div>
                    <div className="student student-5">👩‍🎓</div>
                    <div className="student student-6">👨‍🎓</div>
                  </div>
                  <div className="conformity-text">
                    Same lesson. Different minds.
                  </div>
                </div>
              )}

              {index === 2 && (
                <div className="visual-element obsolete-visual">
                  <div className="skill-decay">
                    <div className="skill-bar">
                      <div className="skill-label">What you learned</div>
                      <div className="skill-progress full"></div>
                    </div>
                    <div className="skill-bar">
                      <div className="skill-label">
                        Still relevant in 2 years
                      </div>
                      <div className="skill-progress half"></div>
                    </div>
                    <div className="skill-bar">
                      <div className="skill-label">
                        Still relevant in 4 years
                      </div>
                      <div className="skill-progress quarter"></div>
                    </div>
                  </div>
                </div>
              )}

              {index === 3 && (
                <div className="visual-element debt-visual">
                  <div className="debt-mountain">
                    <div className="mountain-peak">🏔️</div>
                    <div className="debt-stats">
                      <div className="stat-item">
                        <span className="stat-label">Monthly payment</span>
                        <span className="stat-value">$393</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Total interest</span>
                        <span className="stat-value">$13,589</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {index === 4 && (
                <div className="visual-element collapse-visual">
                  <div className="building-collapse">
                    <div className="building-block block-1">🏛️</div>
                    <div className="building-block block-2">📊</div>
                    <div className="building-block block-3">📉</div>
                  </div>
                  <div className="stats-overlay">
                    <div className="stat">
                      <span className="stat-value">1,200%</span>
                      <span className="stat-label">
                        Tuition increase since 1980
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {index === 5 && (
                <div className="visual-element personalized-visual">
                  <div className="ai-brain-network">
                    <div className="central-brain">🧠</div>
                    <div className="learning-paths">
                      <div className="path path-1"></div>
                      <div className="path path-2"></div>
                      <div className="path path-3"></div>
                    </div>
                    <div className="personalization-tags">
                      <span className="tag">Your pace</span>
                      <span className="tag">Your style</span>
                      <span className="tag">Your goals</span>
                    </div>
                  </div>
                </div>
              )}

              {index === 6 && (
                <div className="visual-element future-visual">
                  <div className="ai-interface">
                    <div className="ai-brain">🧠</div>
                    <div className="connection-lines">
                      <div className="line line-1"></div>
                      <div className="line line-2"></div>
                      <div className="line line-3"></div>
                    </div>
                    <div className="ai-features">
                      <div className="feature">Personalized</div>
                      <div className="feature">Adaptive</div>
                      <div className="feature">24/7 Available</div>
                    </div>
                  </div>
                </div>
              )}

              {index === 7 && (
                <div className="visual-element revolution-visual">
                  <div className="logo-animation">
                    <div className="logo-wrapper">
                      <div className="logo-icon">AI</div>
                      <div className="logo-text">Odyssey</div>
                    </div>
                  </div>
                  <div className="cta-section">
                    <button
                      className="cta-button"
                      onClick={() => (window.location.href = '/register')}
                    >
                      Join the Future
                    </button>
                    <p className="cta-subtext">Start learning for free</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      ))}
    </div>
  );
};
